import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Timer, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format seconds to a human-readable French string.
//  - < 1h  -> "Xmin"
//  - < 24h -> "XhYY"
//  - >= 24h -> "Xj YYh"
function formatDuration(seconds) {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min`;
    const h = Math.floor(m / 60);
    const remM = m % 60;
    if (h < 24) return remM ? `${h}h${String(remM).padStart(2, '0')}` : `${h}h`;
    const d = Math.floor(h / 24);
    const remH = h % 24;
    return remH ? `${d}j ${remH}h` : `${d}j`;
}

// Color thresholds (in seconds)
//  - vert (rapide): < 4h
//  - orange (moyen): 4h - 24h
//  - rouge (lent): > 24h
function getSpeedTier(seconds) {
    const s = Number(seconds) || 0;
    if (s < 4 * 3600) return { tier: 'fast', label: 'Rapide', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' };
    if (s <= 24 * 3600) return { tier: 'mid', label: 'Moyen', cls: 'text-orange-700 bg-orange-50 border-orange-200', dot: 'bg-orange-500' };
    return { tier: 'slow', label: 'Lent', cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' };
}

export default function InventaireTempsEnvoi() {
    const [data, setData] = useState({ users: [], total_devis_envoyes: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API_URL}/api/statistiques/inventaire/temps-envoi`, { withCredentials: true })
            .then((r) => { if (!cancelled) setData(r.data || { users: [], total_devis_envoyes: 0 }); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const sorted = useMemo(() => [...(data.users || [])].sort((a, b) => a.avg_seconds - b.avg_seconds), [data.users]);

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 flex items-center gap-2 text-xs text-gray-400" data-testid="inv-tempo-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des temps moyens d'envoi…
            </div>
        );
    }

    if (!sorted.length) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 flex items-center gap-2 text-xs text-gray-500" data-testid="inv-tempo-empty">
                <Timer className="w-3.5 h-3.5 text-gray-400" />
                <span>Aucun devis envoyé pour le moment — les temps moyens apparaîtront ici dès le premier envoi.</span>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3" data-testid="inv-tempo-bloc">
            <div className="flex items-center gap-2 mb-2">
                <Timer className="w-3.5 h-3.5 text-[#1B365D]" />
                <h3 className="text-xs font-semibold text-[#1B365D] uppercase tracking-wide">Temps moyen d'envoi des devis</h3>
                <span className="text-[10px] text-gray-400 ml-auto">classement du plus rapide au plus lent</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="inv-tempo-cards">
                {sorted.map((u, idx) => {
                    const speed = getSpeedTier(u.avg_seconds);
                    const isWinner = idx === 0 && sorted.length > 1;
                    return (
                        <div
                            key={u.user_id}
                            className={`relative rounded-lg border ${speed.cls} px-2.5 py-2 flex items-center gap-2.5 transition-all hover:shadow-sm`}
                            data-testid={`inv-tempo-card-${u.user_id}`}
                            title={`${u.count} devis · ${speed.label}`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${speed.dot} shrink-0`} />
                            <div className="flex flex-col min-w-0 flex-1">
                                <span className="text-[11px] font-semibold text-[#1B365D] truncate flex items-center gap-1">
                                    {isWinner && <Award className="w-3 h-3 text-[#C9A962]" />}
                                    {u.user_name}
                                </span>
                                <span className="text-sm font-bold leading-none mt-0.5">{formatDuration(u.avg_seconds)}</span>
                            </div>
                            <span className="text-[9px] text-gray-500 bg-white/70 rounded px-1 py-0.5 shrink-0">{u.count}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
